import React, { useContext, useEffect, useState, useCallback, createContext } from 'react';
import { AuthContext } from '../App';
import {
  FeedsClient,
  StreamFeeds,
  useClientConnectedUser,
  useFeedsClient,
} from '@stream-io/feeds-react-sdk';
import { toast } from 'sonner';
import { Loader2, Image as ImageIcon, Send, Heart, MessageCircle, Trash2, X, Hash, Pin, PinOff, Pencil, Check, BarChart3, UserPlus, UserCheck, Users, Globe2, Sparkles, Search } from 'lucide-react';
import { PollWidget, CreatePollModal } from './PollComponents';
import { splitTextWithReferences } from '../utils/bibleReferences';
import { useBibleRefViewer } from './BibleRefViewer';

// ---------------- Helpers de parsing @menciones, #hashtags y URLs ----------------
// Renderiza texto con @menciones, #hashtags resaltados y links clickeables
// Componente: renderiza un fragmento de texto plano detectando referencias bíblicas
const PlainTextWithBibleRefs = ({ text }) => {
  const bibleViewer = useBibleRefViewer();
  if (!text) return null;
  const parts = splitTextWithReferences(text);
  return parts.map((p, i) => {
    if (p.type === 'text') return <React.Fragment key={i}>{p.value}</React.Fragment>;
    return (
      <button
        key={i}
        type="button"
        onClick={(e) => { e.stopPropagation(); bibleViewer.open(p.ref.usfm, p.value); }}
        className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 hover:bg-amber-100 px-1.5 py-0.5 rounded text-[0.95em] font-medium border border-amber-200 transition"
        title="Ver versículo"
        data-testid="bible-ref-link"
      >
        <i className="fas fa-cross text-[10px]" />
        {p.value}
      </button>
    );
  });
};

const renderRichText = (text, onHashtagClick) => {
  if (!text) return null;
  // Regex: captura URLs (http/https/www), @palabra o #palabra
  const parts = text.split(/(https?:\/\/\S+|www\.\S+|@[\w.]+|#[\wáéíóúñÁÉÍÓÚÑ]+)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    // URL clickeable
    if (part.startsWith('http://') || part.startsWith('https://') || part.startsWith('www.')) {
      // Separar puntuación final (.,!?;:) que normalmente no es parte del link
      const trailMatch = part.match(/^([\s\S]*?)([.,!?;:)\]]+)$/);
      const cleanUrl = trailMatch ? trailMatch[1] : part;
      const trail = trailMatch ? trailMatch[2] : '';
      const href = part.startsWith('www.') ? `https://${cleanUrl}` : cleanUrl;
      const display = cleanUrl.length > 60 ? cleanUrl.slice(0, 57) + '…' : cleanUrl;
      return (
        <React.Fragment key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:underline break-all font-medium"
            data-testid="red-social-link"
          >
            {display}<i className="fas fa-external-link-alt text-[10px] ml-1 opacity-70" />
          </a>{trail}
        </React.Fragment>
      );
    }
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-blue-600 font-semibold bg-blue-50 px-1 rounded">
          {part}
        </span>
      );
    }
    if (part.startsWith('#')) {
      return (
        <button
          key={i}
          type="button"
          onClick={() => onHashtagClick?.(part.slice(1))}
          className="text-purple-600 font-semibold hover:underline"
          data-testid="red-social-hashtag"
        >
          {part}
        </button>
      );
    }
    return <PlainTextWithBibleRefs key={i} text={part} />;
  });
};

// Extrae menciones y hashtags del texto
const extractMentionsAndTags = (text) => {
  const mentions = [...(text.matchAll(/@([\w.]+)/g) || [])].map((m) => m[1]);
  const hashtags = [...(text.matchAll(/#([\wáéíóúñÁÉÍÓÚÑ]+)/g) || [])].map((m) => m[1].toLowerCase());
  return { mentions, hashtags };
};

// ---------------- Auto-embeds (YouTube) ----------------
// Extrae el primer URL embebible de YouTube que encuentre
const extractMediaEmbed = (text) => {
  if (!text) return null;
  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID
  const ytMatch = text.match(
    /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/
  );
  if (ytMatch) return { type: 'youtube', id: ytMatch[1] };
  return null;
};

const MediaEmbed = ({ embed }) => {
  if (!embed) return null;
  if (embed.type === 'youtube') {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 bg-black" data-testid="media-embed-youtube">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={`https://www.youtube.com/embed/${embed.id}`}
            title="YouTube"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full"
          />
        </div>
      </div>
    );
  }
  return null;
};

// Comprime una imagen a max 1600px y jpeg 82% para reducir tamaño antes de subir
const compressImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  try {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim && file.size < 1_500_000) {
        URL.revokeObjectURL(url);
        return resolve(file);
      }
      const ratio = Math.min(maxDim / width, maxDim / height, 1);
      const w = Math.round(width * ratio);
      const h = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) return reject(new Error('Compresión falló'));
        const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
        resolve(compressed);
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  } catch (e) {
    reject(e);
  }
});

// Excluye activities que NO pertenecen a la Red Social Global:
//  - Foros de Clase: feed `user:class_<id>` o `custom.scope === 'foro_clase'`
//  - Sala Docente: feed `user:area_profesores` o `custom.scope === 'area_profesores'`
const isForoClaseActivity = (a) => {
  const feeds = a?.feeds || [];
  if (Array.isArray(feeds)) {
    if (feeds.some((f) => typeof f === 'string' && (f.startsWith('user:class_') || f === 'user:area_profesores'))) {
      return true;
    }
  }
  const scope = a?.custom?.scope;
  if (scope === 'foro_clase' || scope === 'area_profesores') return true;
  return false;
};
const filterOutForoClase = (acts) => (acts || []).filter((a) => !isForoClaseActivity(a));


// ---------------- Follow context + Follow button ----------------
// Compartido entre las vistas (Para Ti, Muro Global, Directorio) para no
// re-consultar la API en cada componente.
const FollowContext = createContext({
  followingSet: new Set(),
  toggleFollow: async () => {},
  myStreamId: '',
});

const useFollow = () => useContext(FollowContext);

const FollowButton = ({ targetStreamId, targetEmail, className = '' }) => {
  const { followingSet, toggleFollow, myStreamId } = useFollow();
  const [loading, setLoading] = useState(false);
  if (!targetStreamId || targetStreamId === myStreamId) return null;
  const isFollowing = followingSet.has(targetStreamId);
  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (loading) return;
    setLoading(true);
    try {
      await toggleFollow({ targetStreamId, targetEmail, follow: !isFollowing });
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${
        isFollowing
          ? 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-600 border border-gray-200'
          : 'bg-blue-600 text-white hover:bg-blue-700'
      } ${className}`}
      data-testid={isFollowing ? `follow-btn-following-${targetStreamId}` : `follow-btn-follow-${targetStreamId}`}
      title={isFollowing ? 'Dejar de seguir' : 'Seguir'}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : isFollowing ? (
        <><UserCheck className="w-3 h-3" /> Siguiendo</>
      ) : (
        <><UserPlus className="w-3 h-3" /> Seguir</>
      )}
    </button>
  );
};


const PostCard = ({ activity, onDelete, onHashtagClick, isAdmin, onTogglePin }) => {
  const { API } = useContext(AuthContext);
  const client = useFeedsClient();
  const me = useClientConnectedUser();
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Edición del propio post
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(activity?.text || '');
  const [savingEdit, setSavingEdit] = useState(false);

  const baseLikeCount = activity?.reaction_groups?.like?.count || 0;
  const commentCount = activity?.comment_count || 0;
  const baseUserLiked = !!activity?.own_reactions?.find?.((r) => r.type === 'like');
  // Estado optimista local para el like — el SDK no actualiza el objeto activity
  // cuando usamos queryActivities (consulta global), por eso mantenemos el cambio
  // local hasta el próximo refresh.
  const [likeOverride, setLikeOverride] = useState(null); // null | true | false
  const userLiked = likeOverride === null ? baseUserLiked : likeOverride;
  const likeCount = baseLikeCount + (
    likeOverride === null ? 0 :
    (likeOverride === true && !baseUserLiked) ? 1 :
    (likeOverride === false && baseUserLiked) ? -1 : 0
  );
  const isMine = activity?.user?.id === me?.id;
  const authorName = activity?.user?.name || activity?.user?.id || 'Usuario';
  const authorImage = activity?.user?.image || '';
  const createdAt = activity?.created_at ? new Date(activity.created_at) : null;

  const timeAgo = () => {
    if (!createdAt) return '';
    const mins = Math.floor((Date.now() - createdAt.getTime()) / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `hace ${days}d`;
    return createdAt.toLocaleDateString('es-UY');
  };

  const toggleLike = async () => {
    if (!client || !activity?.id) return;
    // Optimistic update: cambiamos el estado local de inmediato, luego sincronizamos
    const newState = !userLiked;
    setLikeOverride(newState);
    try {
      if (userLiked) {
        await client.deleteActivityReaction({ activity_id: activity.id, type: 'like' });
      } else {
        await client.addActivityReaction({ activity_id: activity.id, type: 'like' });
      }
    } catch (err) {
      console.error('Error like:', err);
      // Revertir si falla
      setLikeOverride(!newState);
      toast.error('No se pudo registrar el "Me gusta"');
    }
  };

  const loadComments = useCallback(async () => {
    if (!client || !activity?.id) return;
    setLoadingComments(true);
    try {
      const res = await client.getComments({ object_id: activity.id, object_type: 'activity', limit: 20 });
      setComments(res?.comments || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [client, activity?.id]);

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !client || !activity?.id) return;
    setSubmittingComment(true);
    try {
      await client.addComment({ comment: commentText.trim(), object_id: activity.id, object_type: 'activity' });
      setCommentText('');
      await loadComments();
    } catch (err) {
      console.error('Error comment:', err);
      toast.error('No se pudo comentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!activity?.id) return;
    if (!window.confirm('¿Eliminar esta publicación?')) return;
    try {
      if (isMine && client) {
        // El propio dueño borra vía SDK
        await client.deleteActivity({ id: activity.id });
      } else if (isAdmin) {
        // Admin borra cualquier post vía backend (credenciales server-side)
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/stream-feeds/delete-activity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ activity_id: activity.id, hard_delete: true }),
        });
        if (!res.ok) throw new Error((await res.json())?.detail || 'delete failed');
      }
      toast.success('Publicación eliminada');
      onDelete?.(activity.id);
    } catch (err) {
      console.error('Error delete:', err);
      toast.error(err?.message || 'No se pudo eliminar');
    }
  };

  // Iniciar edición
  const startEdit = () => {
    setEditedText(activity?.text || '');
    setIsEditing(true);
  };
  const cancelEdit = () => {
    setIsEditing(false);
    setEditedText(activity?.text || '');
  };
  const saveEdit = async () => {
    if (!client || !activity?.id) return;
    const newText = editedText.trim();
    if (!newText) {
      toast.error('El texto no puede estar vacío');
      return;
    }
    if (newText === (activity?.text || '')) {
      setIsEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      // Stream Feeds v3 SDK: usar updateActivityPartial para preservar
      // attachments (imágenes) y custom al editar solo el texto.
      // El método correcto en el SDK v1 es updateActivityPartial (no partialUpdateActivity).
      if (typeof client.updateActivityPartial === 'function') {
        await client.updateActivityPartial({ id: activity.id, set: { text: newText } });
      } else if (typeof client.partialUpdateActivity === 'function') {
        await client.partialUpdateActivity({ id: activity.id, set: { text: newText } });
      } else {
        // Último recurso: updateActivity reemplaza todo el activity; hay que
        // reenviar attachments y custom para no perderlos.
        await client.updateActivity({
          id: activity.id,
          text: newText,
          attachments: activity.attachments || [],
          custom: activity.custom || {},
        });
      }
      // Actualizar optimistic el objeto local (mutación mínima)
      if (activity) activity.text = newText;
      setIsEditing(false);
      toast.success('Publicación editada');
    } catch (err) {
      console.error('Error edit:', err);
      toast.error('No se pudo editar');
    } finally {
      setSavingEdit(false);
    }
  };

  const attachments = activity?.attachments || [];
  const firstImage = attachments.find((a) => a.type === 'image');
  const imageUrl = firstImage?.image_url || firstImage?.asset_url || firstImage?.assetUrl;
  const isPinned = !!activity?.pinned_at || !!activity?.custom?.pinned;

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border mb-4 overflow-hidden ${
        isPinned ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200'
      }`}
      data-testid="red-social-post"
      data-activity-id={activity?.id}
    >
      {isPinned && (
        <div className="bg-gradient-to-r from-amber-100 to-yellow-100 px-4 py-1.5 flex items-center gap-2 text-amber-700 text-xs font-semibold" data-testid="pinned-badge">
          <Pin className="w-3.5 h-3.5" />
          <span>Publicación fijada</span>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {authorImage ? (
              <img src={authorImage} alt={authorName} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                {authorName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-gray-800 truncate">{authorName}</p>
                {!isMine && activity?.user?.id && (
                  <FollowButton targetStreamId={activity.user.id} />
                )}
              </div>
              <p className="text-xs text-gray-500">{timeAgo()}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <button
                onClick={() => onTogglePin?.(activity, !isPinned)}
                className={`p-2 rounded-lg transition-colors ${
                  isPinned
                    ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                    : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                }`}
                data-testid={isPinned ? 'red-social-unpin-post' : 'red-social-pin-post'}
                title={isPinned ? 'Desfijar' : 'Fijar publicación'}
              >
                {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
              </button>
            )}
            {isMine && !isEditing && (
              <button
                onClick={startEdit}
                className="text-gray-400 hover:text-blue-500 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                data-testid="red-social-edit-post"
                title="Editar publicación"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
            {(isMine || isAdmin) && (
              <button
                onClick={handleDelete}
                className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors"
                data-testid="red-social-delete-post"
                title={isMine ? 'Eliminar' : 'Eliminar (admin)'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="mt-3 space-y-2">
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              rows={Math.min(8, Math.max(3, (editedText || '').split('\n').length + 1))}
              maxLength={2000}
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none resize-none"
              data-testid="red-social-edit-textarea"
              autoFocus
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                {editedText.length}/2000 · ✏️ Editando
              </span>
              <div className="flex gap-2">
                <button
                  onClick={cancelEdit}
                  disabled={savingEdit}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 font-medium"
                  data-testid="red-social-edit-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit || !editedText.trim()}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
                  data-testid="red-social-edit-save"
                >
                  {savingEdit ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Guardando...</>
                  ) : (
                    <><Check className="w-3 h-3" /> Guardar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          activity?.text && (
            <>
              <p className="text-gray-800 mt-3 whitespace-pre-wrap break-words" data-testid="red-social-post-body">
                {renderRichText(activity.text, onHashtagClick)}
              </p>
              {(activity?.type === 'poll' && activity?.custom?.poll_id) && (
                <PollWidget pollId={activity.custom.poll_id} initialQuestion={activity.custom.question} initialOptions={activity.custom.options || []} />
              )}
              <MediaEmbed embed={extractMediaEmbed(activity.text)} />
            </>
          )
        )}
      </div>
      {imageUrl && (
        <img src={imageUrl} alt="" className="w-full max-h-[500px] object-cover bg-gray-100" loading="lazy" />
      )}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4">
        <button
          onClick={toggleLike}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-all active:scale-95 ${
            userLiked ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-600 hover:bg-gray-50'
          }`}
          data-testid="red-social-like-btn"
        >
          <Heart className={`w-4 h-4 ${userLiked ? 'fill-current' : ''}`} />
          <span>{likeCount > 0 ? likeCount : ''} {userLiked ? 'Me gusta' : 'Me gusta'}</span>
        </button>
        <button
          onClick={() => setShowComments((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-all active:scale-95"
          data-testid="red-social-toggle-comments"
        >
          <MessageCircle className="w-4 h-4" />
          <span>{commentCount > 0 ? commentCount : ''} Comentar</span>
        </button>
      </div>
      {showComments && (
        <div className="bg-gray-50 border-t border-gray-100 p-4">
          {loadingComments ? (
            <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {comments.length === 0 && <p className="text-sm text-gray-500 italic text-center py-2">Sin comentarios todavía</p>}
              {comments.map((c) => (
                <div key={c.id} className="bg-white rounded-lg px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-gray-800 text-xs">{c.user?.name || 'Usuario'}</span>
                    <span className="text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleDateString('es-UY') : ''}</span>
                  </div>
                  <p className="text-gray-700">{renderRichText(c.comment || c.text, onHashtagClick)}</p>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={submitComment} className="flex items-center gap-2 mt-3">
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Escribí un comentario..."
              className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
              data-testid="red-social-comment-input"
              disabled={submittingComment}
            />
            <button
              type="submit"
              disabled={submittingComment || !commentText.trim()}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors"
              data-testid="red-social-comment-submit"
            >
              {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// ---------------- Timeline personalizado (Para Ti) ----------------
// Muestra posts solo de la gente que el usuario sigue. Usa el feed timeline:{my_id}
// que Stream Feeds llena automáticamente con los posts de los user:{X} seguidos.
const PersonalTimelineFeed = ({ userName, userImage, userRole }) => {
  const client = useFeedsClient();
  const me = useClientConnectedUser();
  const { followingSet } = useFollow();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineFeed, setTimelineFeed] = useState(null);

  useEffect(() => {
    if (!client || !me?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const tFeed = client.feed('timeline', me.id);
        await tFeed.getOrCreate({ watch: true, limit: 30 });
        if (cancelled) return;
        setTimelineFeed(tFeed);
        const initial = filterOutForoClase(tFeed.state.getLatestValue()?.activities || []);
        setActivities(initial);
        setLoading(false);

        const unsub = tFeed.state.subscribe((s) => {
          if (cancelled) return;
          setActivities(filterOutForoClase(s.activities || []));
        });
        return () => unsub?.();
      } catch (err) {
        console.error('Error cargando timeline personal:', err);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client, me?.id]);

  // Cuando cambia followingSet, refrescar el feed (Stream agrega/quita actividades
  // del timeline asincrónicamente, pero un getOrCreate inmediato ayuda a reflejar follows nuevos).
  useEffect(() => {
    if (!timelineFeed) return;
    timelineFeed.getOrCreate({ watch: true, limit: 30 }).catch(() => {});
  }, [followingSet.size, timelineFeed]);

  const handleDelete = (id) => setActivities((prev) => prev.filter((a) => a.id !== id));
  const handleTogglePin = async () => {}; // sin pin en timeline personal
  const isAdmin = userRole === 'admin';

  const visible = activities.slice().sort(
    (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
  );

  return (
    <div className="max-w-2xl mx-auto">
      {followingSet.size === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-800 flex items-center gap-3" data-testid="timeline-empty-no-follows">
          <Sparkles className="w-5 h-5 flex-shrink-0" />
          <p>Todavía no seguís a nadie. Andá a <strong>Descubrir</strong> para encontrar profesores y compañeros.</p>
        </div>
      )}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm text-gray-500 mt-3">Cargando tu feed...</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center" data-testid="timeline-empty">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Sin publicaciones nuevas de la gente que seguís.</p>
          <p className="text-xs text-gray-400 mt-2">Probá el <strong>Muro Global</strong> para ver todo o seguí a más personas en <strong>Descubrir</strong>.</p>
        </div>
      ) : (
        <div data-testid="timeline-personal-activities">
          {visible.map((a) => (
            <PostCard
              key={a.id}
              activity={a}
              onDelete={handleDelete}
              onHashtagClick={() => {}}
              isAdmin={isAdmin}
              onTogglePin={handleTogglePin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------- Directorio de usuarios (Descubrir) ----------------
const UserDirectory = () => {
  const { API } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { followingSet } = useFollow();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/stream-feeds/users-directory`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('No se pudo cargar el directorio');
        const data = await res.json();
        if (!cancelled) setUsers(data.users || []);
      } catch (err) {
        console.error('Directorio error:', err);
        toast.error('No se pudo cargar el directorio');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  // Mantenemos la lista alineada con followingSet (que es la fuente de verdad)
  const decoratedUsers = users.map((u) => ({
    ...u,
    is_following: followingSet.has(u.id),
  }));

  const filtered = decoratedUsers
    .filter((u) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      // Profesores y admin primero (sugerencias), luego siguiendo, luego alfabético
      const rolePriority = (r) => (r === 'admin' ? 0 : r === 'profesor' ? 1 : 2);
      const ar = rolePriority(a.role);
      const br = rolePriority(b.role);
      if (ar !== br) return ar - br;
      return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
    });

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 mb-4 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-800">Descubrir personas</h2>
          <span className="ml-auto text-xs text-gray-500" data-testid="following-count">
            Siguiendo: {followingSet.size}
          </span>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
            data-testid="directory-search"
          />
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-8">Sin resultados</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100" data-testid="directory-list">
          {filtered.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-3" data-testid={`directory-user-${u.id}`}>
              {u.image ? (
                <img src={u.image} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                  {(u.name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm truncate">{u.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {u.role === 'admin' ? '👑 Admin' : u.role === 'profesor' ? '🎓 Profesor' : 'Estudiante'}
                </p>
              </div>
              <FollowButton targetStreamId={u.id} targetEmail={u.email} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ---------------- Timeline / Feed público ----------------
const GlobalFeed = ({ userName, userImage, userRole }) => {
  const { API } = useContext(AuthContext);
  const client = useFeedsClient();
  const me = useClientConnectedUser();
  const [activities, setActivities] = useState([]);
  // Deep-link via ?activityId=xxx — scroll y highlight cuando viene de push notification
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('activityId');
    if (!targetId || activities.length === 0) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-activity-id="${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2', 'transition-all');
        setTimeout(() => el.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2'), 3000);
      }
      // Limpiar query
      const url = new URL(window.location.href);
      url.searchParams.delete('activityId');
      window.history.replaceState({}, '', url.toString());
    }, 500);
  }, [activities]);
  const [loading, setLoading] = useState(true);
  const [myFeed, setMyFeed] = useState(null);

  // Al montar: crear el feed propio del usuario (donde él puede publicar)
  // y cargar TODAS las actividades del grupo user (query global)
  useEffect(() => {
    if (!client || !me?.id) return;
    let cancelled = false;
    (async () => {
      try {
        // Feed propio del usuario - acá sí puede postear (es dueño)
        const ownFeed = client.feed('user', me.id);
        await ownFeed.getOrCreate({ watch: true });
        if (cancelled) return;
        setMyFeed(ownFeed);

        // Cargar todas las actividades de la Red (muro compartido)
        const queryRes = await client.queryActivities({
          sort: [{ field: 'created_at', direction: -1 }],
          limit: 30,
        });
        if (cancelled) return;
        setActivities(filterOutForoClase(queryRes?.activities || []));
        setLoading(false);

        // Suscribirse al feed propio para detectar nuevos posts y agregarlos localmente
        const unsub = ownFeed.state.subscribe((s) => {
          const myNewActs = filterOutForoClase(s.activities || []);
          setActivities((prev) => {
            const ids = new Set(prev.map((a) => a.id));
            const incoming = myNewActs.filter((a) => !ids.has(a.id));
            if (incoming.length === 0) return prev;
            return [...incoming, ...prev];
          });
        });
        return () => unsub?.();
      } catch (err) {
        console.error('Error cargando Red IBA:', err);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client, me?.id]);

  // Refresca toda la muralla desde el servidor
  const reloadAll = useCallback(async () => {
    if (!client) return;
    try {
      const queryRes = await client.queryActivities({
        sort: [{ field: 'created_at', direction: -1 }],
        limit: 30,
      });
      setActivities(filterOutForoClase(queryRes?.activities || []));
    } catch (_e) {}
  }, [client]);

  // Publicar en mi propio feed (user:{myId}) - así siempre tengo permisos
  const handleCreatePost = useCallback(async (text, imageFile) => {
    if (!myFeed) throw new Error('El feed aún no está listo');
    const attachments = [];
    if (imageFile && client) {
      try {
        const compressed = await compressImage(imageFile).catch(() => imageFile);
        const uploaded = await client.uploadFile({ file: compressed });
        const url = uploaded?.file || uploaded?.url || uploaded?.data?.file;
        if (url) attachments.push({ type: 'image', image_url: url, custom: {} });
        else console.warn('Upload sin URL:', uploaded);
      } catch (err) {
        console.error('Upload error:', err);
        throw new Error('No se pudo subir la imagen');
      }
    }
    const { mentions, hashtags } = extractMentionsAndTags(text || '');
    const res = await myFeed.addActivity({
      type: 'post',
      text: (text || '').trim(),
      attachments,
      custom: { mentions, hashtags },
    });

    // Refresh del muro global tras publicar
    setTimeout(reloadAll, 700);

    // Push a mencionados (más específico)
    let mentionedUserIds = [];
    if (mentions.length > 0) {
      try {
        const token = localStorage.getItem('token');
        const mentionRes = await fetch(`${API}/stream-feeds/mention-notify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            mentions,
            text: (text || '').trim().slice(0, 200),
            activity_id: res?.activity?.id || res?.id || null,
          }),
        });
        if (mentionRes.ok) {
          const mentionData = await mentionRes.json();
          mentionedUserIds = mentionData?.user_ids || [];
        }
      } catch (err) {
        console.warn('No se pudo enviar push de mención:', err);
      }
    }

    // Push a todos los demás usuarios activos (excluyendo autor y mencionados)
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/stream-feeds/new-post-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          text: (text || '').trim().slice(0, 200),
          activity_id: res?.activity?.id || res?.id || null,
          has_image: attachments.length > 0,
          exclude_user_ids: mentionedUserIds,
        }),
      });
    } catch (err) {
      console.warn('No se pudo enviar push de nuevo post:', err);
    }
  }, [myFeed, client, API, reloadAll]);

  const handleDelete = (id) => {
    setActivities((prev) => prev.filter((a) => a.id !== id));
  };

  // Toggle pin (solo admin) - ahora el feed es user:{ownerId}
  const handleTogglePin = async (activity, pin) => {
    try {
      const token = localStorage.getItem('token');
      const ownerId = activity?.user?.id || activity?.actor?.id || me?.id;
      const res = await fetch(`${API}/stream-feeds/pin-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          activity_id: activity.id,
          feed: `user:${ownerId}`,
          pin,
        }),
      });
      if (!res.ok) throw new Error('pin failed');
      toast.success(pin ? 'Publicación fijada' : 'Publicación desfijada');
      setActivities((prev) => prev.map((a) => (
        a.id === activity.id ? { ...a, pinned_at: pin ? new Date().toISOString() : null } : a
      )));
    } catch (err) {
      console.error('Error pin:', err);
      toast.error('No se pudo actualizar el pin');
    }
  };

  // Filtro por hashtag
  const [activeHashtag, setActiveHashtag] = useState(null);
  const handleHashtagClick = (tag) => {
    setActiveHashtag((cur) => (cur === tag ? null : tag));
  };
  const isAdmin = (userRole === 'admin');
  const visibleActivities = (activeHashtag
    ? activities.filter((a) => (a?.custom?.hashtags || []).includes(activeHashtag.toLowerCase()))
    : activities
  ).slice().sort((a, b) => {
    const aPin = !!a?.pinned_at || !!a?.custom?.pinned;
    const bPin = !!b?.pinned_at || !!b?.custom?.pinned;
    if (aPin !== bPin) return aPin ? -1 : 1;
    return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
  });

  return (
    <div className="max-w-2xl mx-auto">
      <GlobalComposer onSubmit={handleCreatePost} userName={userName} userImage={userImage} userRole={userRole} onPollCreated={() => setTimeout(reloadAll, 700)} />
      {activeHashtag && (
        <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl px-4 py-2 mb-4" data-testid="hashtag-filter-active">
          <div className="flex items-center gap-2 text-purple-700">
            <Hash className="w-4 h-4" />
            <span className="font-semibold">#{activeHashtag}</span>
            <span className="text-sm text-purple-500">· {visibleActivities.length} resultado{visibleActivities.length !== 1 ? 's' : ''}</span>
          </div>
          <button
            onClick={() => setActiveHashtag(null)}
            className="text-xs px-2 py-1 rounded-lg bg-white hover:bg-purple-100 text-purple-700 border border-purple-200 transition-colors"
            data-testid="clear-hashtag-filter"
          >
            Quitar filtro
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm text-gray-500 mt-3">Cargando la Red IBA...</p>
        </div>
      ) : visibleActivities.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
          <i className="fas fa-users text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500">
            {activeHashtag ? `No hay publicaciones con #${activeHashtag}` : 'Todavía no hay publicaciones. ¡Sé el primero!'}
          </p>
        </div>
      ) : (
        <div data-testid="red-social-activities">
          {visibleActivities.map((a) => (
            <PostCard
              key={a.id}
              activity={a}
              onDelete={handleDelete}
              onHashtagClick={handleHashtagClick}
              isAdmin={isAdmin}
              onTogglePin={handleTogglePin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Composer que publica directamente al feed global (con @menciones y #hashtags)
const GlobalComposer = ({ onSubmit, userName, userImage, userRole, onPollCreated }) => {
  const { API } = useContext(AuthContext);
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [showPollModal, setShowPollModal] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = React.useRef(null);

  // Autocomplete de @menciones
  const [allUsers, setAllUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // {start, query} o null
  const [mentionIndex, setMentionIndex] = useState(0);

  // Cargar lista de usuarios una vez para el autocomplete
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/stream-chat/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAllUsers(data.users || []);
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, [API]);

  // Detecta si el cursor está escribiendo una @mención
  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart || 0;
    // Buscar la última @ antes del caret (sin espacio entre medio)
    const before = val.slice(0, caret);
    const match = before.match(/@([\w.]*)$/);
    if (match) {
      setMentionQuery({ start: caret - match[0].length, query: match[1].toLowerCase() });
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredUsers = mentionQuery
    ? allUsers
        .filter((u) => {
          const q = mentionQuery.query;
          if (!q) return true;
          return (
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
          );
        })
        // Orden alfabético (case-insensitive, soporta acentos)
        .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        )
        // Limitar a 50 para no sobrecargar el DOM (en la vida real un usuario
        // tipea 1-2 letras antes de elegir; con 50 cubre cualquier instituto)
        .slice(0, 50)
    : [];

  const pickMention = (u) => {
    if (!mentionQuery) return;
    // Reemplazar @queryText por @Nombre_Apellido
    const handle = (u.name || u.email || 'usuario').replace(/\s+/g, '_');
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    const newText = `${before}@${handle} ${after}`;
    setText(newText);
    setMentionQuery(null);
    // Restaurar foco al textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = before.length + handle.length + 2;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 10);
  };

  const handleKeyDown = (e) => {
    if (!mentionQuery || filteredUsers.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pickMention(filteredUsers[mentionIndex]);
    } else if (e.key === 'Escape') {
      setMentionQuery(null);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('La imagen debe pesar menos de 15 MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !imageFile) {
      toast.error('Escribí algo o adjuntá una imagen');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(text, imageFile);
      setText('');
      clearImage();
      toast.success('Publicado en la Red IBA');
    } catch (err) {
      console.error('Publicar error:', err);
      const msg = err?.message || err?.body?.message || 'No se pudo publicar';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4" data-testid="red-social-global-composer">
      <div className="flex items-start gap-3">
        {userImage ? (
          <img src={userImage} alt={userName} className="w-10 h-10 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold shrink-0">
            {(userName || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={`¿Qué querés compartir con la comunidad IBA, ${(userName || '').split(' ')[0] || 'hermano'}? Usá @ para mencionar y # para etiquetar`}
            rows={3}
            maxLength={1500}
            className="w-full resize-none border-0 focus:ring-0 focus:outline-none text-gray-800 placeholder-gray-400"
            data-testid="red-social-post-text"
            disabled={submitting}
          />

          {/* Dropdown de sugerencias de menciones */}
          {mentionQuery && filteredUsers.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-20" data-testid="mention-dropdown">
              <div className="max-h-72 overflow-y-auto">
                {filteredUsers.map((u, idx) => (
                  <button
                    type="button"
                    key={u.id || u.email || idx}
                    onClick={() => pickMention(u)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                      idx === mentionIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {u.foto_perfil ? (
                      <img src={u.foto_perfil} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white text-xs font-bold flex items-center justify-center">
                        {(u.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {imagePreview && (
            <div className="relative mt-2 rounded-lg overflow-hidden border border-gray-200">
              <img src={imagePreview} alt="" className="w-full max-h-80 object-cover" />
              <button type="button" onClick={clearImage} className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1">
          <label className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 cursor-pointer px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
            <ImageIcon className="w-4 h-4" />
            <span>Foto</span>
            <input type="file" accept="image/*" onChange={handleImagePick} className="hidden" disabled={submitting} />
          </label>
          {(userRole === 'admin' || userRole === 'profesor') && (
            <button
              type="button"
              onClick={() => setShowPollModal(true)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 px-3 py-1.5 rounded-lg hover:bg-purple-50 transition-colors"
              data-testid="red-social-create-poll-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Encuesta</span>
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting || (!text.trim() && !imageFile)}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-sm hover:shadow-md disabled:opacity-50 transition-all active:scale-95"
          data-testid="red-social-submit-post"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Publicar
        </button>
      </div>
      {(userRole === 'admin' || userRole === 'profesor') && (
        <CreatePollModal
          isOpen={showPollModal}
          onClose={() => setShowPollModal(false)}
          onCreated={() => onPollCreated?.()}
          API={API}
        />
      )}
    </form>
  );
};

// ---------------- Wrapper que conecta el cliente ----------------
const RedSocialIBA = () => {
  const { API } = useContext(AuthContext);
  const [client, setClient] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [activeTab, setActiveTab] = useState('global'); // 'for-you' | 'global' | 'discover'

  useEffect(() => {
    let mounted = true;
    let fClient = null;

    const init = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No autenticado');
        const res = await fetch(`${API}/stream-feeds/token`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('No se pudo obtener token');
        const data = await res.json();
        if (!mounted) return;

        fClient = new FeedsClient(data.api_key);
        await fClient.connectUser(
          { id: data.user_id, name: data.user_name, image: data.user_image || undefined },
          data.token
        );
        if (!mounted) return;
        setClient(fClient);
        setTokenData(data);

        // Cargar lista de "siguiendo" (incluye auto-follow de profesores recién hecho en el token)
        try {
          const fRes = await fetch(`${API}/stream-feeds/following`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (fRes.ok) {
            const fData = await fRes.json();
            if (mounted) setFollowingSet(new Set(fData.following || []));
          }
        } catch (_e) {}
      } catch (err) {
        console.error('Error Red Social:', err);
        if (mounted) setError(err.message || 'Error');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();

    return () => {
      mounted = false;
      try { fClient?.disconnectUser?.(); } catch (_e) {}
    };
  }, []); // eslint-disable-line

  // toggleFollow llamable desde cualquier FollowButton
  const toggleFollow = useCallback(async ({ targetStreamId, targetEmail, follow }) => {
    const token = localStorage.getItem('token');
    // Optimistic update
    setFollowingSet((prev) => {
      const next = new Set(prev);
      if (follow) next.add(targetStreamId); else next.delete(targetStreamId);
      return next;
    });
    try {
      const path = follow ? 'follow' : 'unfollow';
      const body = targetEmail ? { target_email: targetEmail } : { target_user_id: targetStreamId };
      const res = await fetch(`${API}/stream-feeds/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('failed');
      toast.success(follow ? 'Ahora estás siguiendo' : 'Dejaste de seguir');
    } catch (err) {
      // Rollback
      setFollowingSet((prev) => {
        const next = new Set(prev);
        if (follow) next.delete(targetStreamId); else next.add(targetStreamId);
        return next;
      });
      toast.error(follow ? 'No se pudo seguir' : 'No se pudo dejar de seguir');
    }
  }, [API]);

  const followCtxValue = React.useMemo(() => ({
    followingSet,
    toggleFollow,
    myStreamId: tokenData?.user_id || '',
  }), [followingSet, toggleFollow, tokenData?.user_id]);

  if (loading) {    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="red-social-loading">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
        <p className="text-gray-600 mt-4">Conectando a la Red IBA Uruguay...</p>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="red-social-error">
        <i className="fas fa-exclamation-triangle text-red-500 text-3xl mb-3"></i>
        <p className="text-red-700 font-semibold">No se pudo cargar la Red IBA</p>
        <p className="text-sm text-red-600 mt-1">{error || 'Error desconocido'}</p>
        <p className="text-xs text-gray-500 mt-3">Verificá que tu cuenta de Stream tenga Activity Feeds habilitado.</p>
      </div>
    );
  }

  const tabBtnCls = (id) => `flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-all ${
    activeTab === id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
  }`;

  return (
    <StreamFeeds client={client}>
      <FollowContext.Provider value={followCtxValue}>
        <div className="py-4">
          <div className="max-w-2xl mx-auto mb-4 text-center">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-testid="red-social-title">
              Red Social IBA Uruguay
            </h1>
            <p className="text-gray-600 text-sm mt-1">Compartí con la comunidad del Instituto</p>
          </div>
          <div className="max-w-2xl mx-auto mb-4 px-2">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
              <button onClick={() => setActiveTab('for-you')} className={tabBtnCls('for-you')} data-testid="red-social-tab-for-you">
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Para Ti</span>
              </button>
              <button onClick={() => setActiveTab('global')} className={tabBtnCls('global')} data-testid="red-social-tab-global">
                <Globe2 className="w-4 h-4" />
                <span className="hidden sm:inline">Muro Global</span>
              </button>
              <button onClick={() => setActiveTab('discover')} className={tabBtnCls('discover')} data-testid="red-social-tab-discover">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Descubrir</span>
              </button>
            </div>
          </div>
          {activeTab === 'for-you' && (
            <PersonalTimelineFeed userName={tokenData?.user_name} userImage={tokenData?.user_image} userRole={tokenData?.user_role} />
          )}
          {activeTab === 'global' && (
            <GlobalFeed userName={tokenData?.user_name} userImage={tokenData?.user_image} userRole={tokenData?.user_role} />
          )}
          {activeTab === 'discover' && (
            <UserDirectory />
          )}
        </div>
      </FollowContext.Provider>
    </StreamFeeds>
  );
};

export default RedSocialIBA;
